import { Layout } from "@/components/Layout";

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserProfile, ProfileSettings } from "@/lib/types";
import { useState, useEffect } from "react";
import { ProfileMainView } from "@/components/profile/ProfileMainView";
import { EditProfileView } from "@/components/profile/EditProfileView";
import { LikedPostsView } from "@/components/profile/LikedPostsView";
import { CommentsView } from "@/components/profile/CommentsView";
import { SettingsView } from "@/components/profile/SettingsView";
import { InviteFriendsView } from "@/components/profile/InviteFriendsView";
import { FriendsList } from "@/components/friends/FriendsList";
import { VerificationCenter } from "@/components/verification/VerificationCenter";

type ViewState = 'profile' | 'liked-posts' | 'comments' | 'settings' | 'invite-friends' | 'friends' | 'verification';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>('profile');
  const { toast } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [editForm, setEditForm] = useState<UserProfile | null>(null);

  useEffect(() => {
    const initializeProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) return;

      const initialProfile: UserProfile = {
        id: user.id,
        username: profileData.username || "User Name",
        handle: profileData.handle || "@username",
        bio: "Spreading positivity and making the world a better place!",
        avatar_url: profileData.avatar_url || "",
        settings: {
          is_public: true,
          anonymous_mode: false,
          show_statistics: true
        },
        stats: profileData.stats as any
      };

      setProfile(initialProfile);
      setEditForm(initialProfile);
    };

    initializeProfile();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from('profiles')
        .update({
          username: editForm?.username,
          handle: editForm?.handle,
          bio: editForm?.bio,
          settings: editForm?.settings
        })
        .eq('id', user.id);

      if (error) throw error;

      if (editForm) {
        setProfile(editForm);
      }
      setIsEditing(false);
      toast({
        title: "Profile updated! 🎉",
        description: "Your changes have been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const toggleSetting = (setting: keyof ProfileSettings) => {
    if (!editForm) return;
    setEditForm(prev => ({
      ...prev!,
      settings: {
        ...prev!.settings,
        [setting]: !prev!.settings[setting]
      }
    }));
  };

  const handleProfileChange = (field: keyof UserProfile, value: string) => {
    if (!editForm) return;
    setEditForm(prev => ({
      ...prev!,
      [field]: value
    }));
  };

  const handleInviteFriends = () => {
    setCurrentView('invite-friends');
  };

  const handleSettings = () => {
    setCurrentView('settings');
  };

  const handleFriends = () => {
    setCurrentView('friends');
  };

  const handleVerification = () => {
    setCurrentView('verification');
  };

  const renderContent = () => {
    if (!profile || !editForm) return null;

    if (isEditing) {
      return (
        <EditProfileView
          profile={editForm}
          onSave={handleSaveProfile}
          onCancel={() => setIsEditing(false)}
          onChange={handleProfileChange}
        />
      );
    }

    switch (currentView) {
      case 'liked-posts':
        return <LikedPostsView onBack={() => setCurrentView('profile')} />;
      case 'comments':
        return <CommentsView onBack={() => setCurrentView('profile')} />;
      case 'settings':
        return (
          <SettingsView
            settings={editForm.settings}
            onBack={() => setCurrentView('profile')}
            onSettingToggle={toggleSetting}
            onClose={() => setCurrentView('profile')}
          />
        );
      case 'invite-friends':
        return <InviteFriendsView onBack={() => setCurrentView('profile')} />;
      case 'friends':
        return (
          <div>
            <button 
              onClick={() => setCurrentView('profile')} 
              className="mb-4 text-blue-600 hover:text-blue-800"
            >
              ← Back to Profile
            </button>
            <FriendsList userId={profile.id} />
          </div>
        );
      case 'verification':
        return (
          <div>
            <button 
              onClick={() => setCurrentView('profile')} 
              className="mb-4 text-blue-600 hover:text-blue-800"
            >
              ← Back to Profile
            </button>
            <VerificationCenter userId={profile.id} />
          </div>
        );
      default:
        return (
          <ProfileMainView
            profile={profile}
            onEdit={() => setIsEditing(true)}
            onInviteFriends={handleInviteFriends}
            onSettings={handleSettings}
            onFriends={handleFriends}
            onVerification={handleVerification}
          />
        );
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
};

export default Profile;